import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import Text from "@components/Text";
import Button from "@components/Button";
import Spinner from "@components/Spinner";
import {
  getCalibrations,
  AdminCalibration,
  updateCalibrationCoefficient,
  updateCalibrationStatus,
  CalibrationWorkflowStatus,
} from "@services/adminService";
import {
  calibrationRecorderLabel,
  calibrationStatusKind,
  formatCoefficient,
  isMobileCalibration,
  phoneCoefficientOf,
} from "@src/utils/calibrationDisplay";

const Table = styled.div`
  width: 100%;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 80px 1.5fr 120px 120px 100px 110px 80px 80px;
  padding: 12px 16px;
  border-bottom: 1px solid #ebeeef;
  gap: 8px;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 80px 1.5fr 120px 120px 100px 110px 80px 80px;
  padding: 12px 16px;
  border-bottom: 1px solid #f5f5f5;
  align-items: center;
  gap: 8px;
  &:hover {
    background: #fafafa;
  }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
  padding-bottom: 32px;
`;

const PageButton = styled(Button)`
  width: auto;
  min-width: 80px;
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const CoeffCell = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  border-radius: 4px;
  padding: 2px 4px;
  margin: -2px -4px;
  &:hover {
    background: #f0f0f0;
  }
`;

const CoeffInput = styled.input`
  width: 80px;
  padding: 2px 6px;
  border: 1px solid #4caf50;
  border-radius: 4px;
  font-size: 13px;
  outline: none;
  &:focus {
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }
`;

const EditIcon = styled.span`
  font-size: 11px;
  color: #899093;
  opacity: 0;
  ${CoeffCell}:hover & {
    opacity: 1;
  }
`;

const CalibrationErrorBlock = styled.div`
  padding: 8px 16px 14px;
  margin-top: -4px;
  border-bottom: 1px solid #f5f5f5;
  background: #fff5f5;
`;

const CalibrationErrorText = styled.pre`
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 11px;
  line-height: 1.45;
  color: #8b1538;
  white-space: pre-wrap;
  word-break: break-word;
`;

const MobileBadge = styled.span`
  display: inline-block;
  padding: 0 5px;
  border-radius: 4px;
  background: #eef6e3;
  color: #68ad18;
  font-size: 9px;
  font-weight: bold;
  text-transform: uppercase;
  white-space: nowrap;
`;

const CellSub = styled.div`
  font-size: 11px;
  line-height: 15px;
  color: #899093;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusSelect = styled.select`
  font-size: 11px;
  padding: 2px 4px;
  border-radius: 4px;
  border: 1px solid #cbd5d8;
  max-width: 118px;
  cursor: pointer;
  background: #fff;
`;

const PAGE_SIZE = 50;

interface Props {
  esn: string;
}

const DeviceCalibrationsSubTab: React.FC<Props> = ({ esn }) => {
  const [calibrationsList, setCalibrationsList] = useState<AdminCalibration[]>(
    []
  );
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [statusSavingId, setStatusSavingId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const STATUS_OPTIONS: { value: CalibrationWorkflowStatus; label: string }[] = [
    { value: "pending", label: "Pending" },
    { value: "computing", label: "Computing" },
    { value: "completed", label: "Completed" },
    { value: "failed", label: "Failed" },
  ];

  const fetchCalibrations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCalibrations({
        esn,
        limit: PAGE_SIZE,
        offset,
      });
      setCalibrationsList(data.calibrations);
      setTotal(data.total);
    } catch (e) {
      console.error("Failed to fetch calibrations:", e);
    } finally {
      setLoading(false);
    }
  }, [esn, offset]);

  useEffect(() => {
    fetchCalibrations();
  }, [fetchCalibrations]);

  const startEditing = (cal: AdminCalibration) => {
    setEditingId(cal.id);
    setEditValue(cal.coefficient != null ? String(cal.coefficient) : "");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const saveCoefficient = async () => {
    if (!editingId) return;
    const num = parseFloat(editValue);
    if (isNaN(num) || num <= 0) {
      setEditingId(null);
      return;
    }
    setSaving(true);
    try {
      await updateCalibrationCoefficient(editingId, num);
      setCalibrationsList((prev) =>
        prev.map((c) =>
          c.id === editingId
            ? { ...c, coefficient: num, status: "completed", error: null }
            : c
        )
      );
    } catch (e) {
      console.error("Failed to update coefficient:", e);
    } finally {
      setSaving(false);
      setEditingId(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") saveCoefficient();
    if (e.key === "Escape") setEditingId(null);
  };

  const handleStatusChange = async (
    cal: AdminCalibration,
    next: CalibrationWorkflowStatus
  ) => {
    const current = (cal.status ?? "pending") as CalibrationWorkflowStatus;
    if (next === current) return;
    setStatusSavingId(cal.id);
    try {
      await updateCalibrationStatus(cal.id, {
        status: next,
        ...(next === "completed" ? { error: null } : {}),
      });
      setCalibrationsList((prev) =>
        prev.map((c) =>
          c.id === cal.id
            ? {
                ...c,
                status: next,
                error: next === "completed" ? null : c.error,
              }
            : c
        )
      );
    } catch (e) {
      console.error("Failed to update calibration status:", e);
    } finally {
      setStatusSavingId(null);
    }
  };

  const formatInterval = (start: string, stop: string) => {
    const fmt = (iso: string) => {
      const d = new Date(iso);
      return d.toLocaleString("sv-SE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
      });
    };
    return `${fmt(start)} \u2013 ${fmt(stop)}`;
  };

  if (loading) {
    return (
      <LoadingWrap>
        <Spinner />
      </LoadingWrap>
    );
  }

  if (calibrationsList.length === 0) {
    return (
      <Text size={13} color="#899093" style={{ padding: "24px 0" }}>
        No calibrations found for this device.
      </Text>
    );
  }

  return (
    <div>
      <Table>
        <TableHeader>
          <Text size={12} color="#899093" type="heading">
            ID
          </Text>
          <Text size={12} color="#899093" type="heading">
            INTERVAL
          </Text>
          <Text size={12} color="#899093" type="heading">
            WEIGHT
          </Text>
          <Text size={12} color="#899093" type="heading">
            REAPER WIDTH
          </Text>
          <Text size={12} color="#899093" type="heading">
            REVISION
          </Text>
          <Text size={12} color="#899093" type="heading">
            COEFF
          </Text>
          <Text size={12} color="#899093" type="heading">
            STATUS
          </Text>
          <Text size={12} color="#899093" type="heading">
            TZ
          </Text>
        </TableHeader>

        {calibrationsList.map((cal) => {
          const recorder = calibrationRecorderLabel(cal);
          const phoneCoefficient = phoneCoefficientOf(cal);
          return (
            <React.Fragment key={cal.id}>
              <TableRow>
                <div>
                  <Text size={12} color="#899093">
                    {cal.id.substring(0, 8)}
                  </Text>
                  {isMobileCalibration(cal) && (
                    <MobileBadge title="Recorded in the driver app">Mobile</MobileBadge>
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <Text size={13} color="#494c5b">
                    {formatInterval(cal.start, cal.stop)}
                  </Text>
                  {recorder != null && (
                    <CellSub title={cal.recordedBy?.email}>by {recorder}</CellSub>
                  )}
                  {cal.fieldId === null && (
                    <CellSub title="The server matched this run to none of the owner's fields">
                      no field
                    </CellSub>
                  )}
                </div>
                <Text size={13} color="#494c5b">
                  {cal.weightKilograms != null
                    ? `${cal.weightKilograms} kg`
                    : "-"}
                </Text>
                <Text size={13} color="#494c5b">
                  {cal.reaperWidthMeters != null
                    ? `${cal.reaperWidthMeters} m`
                    : "-"}
                </Text>
                <Text size={13} color="#494c5b">
                  {cal.revision ?? "-"}
                </Text>
                <div>
                  {editingId === cal.id ? (
                    <CoeffInput
                      ref={inputRef}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={saveCoefficient}
                      onKeyDown={handleKeyDown}
                      disabled={saving}
                    />
                  ) : (
                    <CoeffCell onClick={() => startEditing(cal)}>
                      <Text size={13} color={cal.coefficient != null ? "#417900" : "#899093"}>
                        {cal.coefficient != null ? cal.coefficient.toFixed(4) : "-"}
                      </Text>
                      <EditIcon>✎</EditIcon>
                    </CoeffCell>
                  )}
                  {phoneCoefficient != null && (
                    <CellSub title="The phone's own coefficient, reference only">
                      phone {formatCoefficient(phoneCoefficient)}
                    </CellSub>
                  )}
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <StatusSelect
                    value={(cal.status ?? "pending") as CalibrationWorkflowStatus}
                    disabled={statusSavingId === cal.id}
                    onChange={(e) =>
                      handleStatusChange(
                        cal,
                        e.target.value as CalibrationWorkflowStatus
                      )
                    }
                    title="Workflow status: Completed is required for analytics to use this calibration as latest"
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </StatusSelect>
                  {calibrationStatusKind(cal) === "waiting" && (
                    <CellSub title="Waiting for machine data. Driver-app run: the job computes it once stop is 15+ min old and telemetry has arrived (retries up to 24 h, then failed)">
                      awaiting data
                    </CellSub>
                  )}
                </div>
                <Text size={13} color="#494c5b">
                  {cal.deviceTimezoneSeconds != null
                    ? `UTC${cal.deviceTimezoneSeconds >= 0 ? "+" : ""}${Math.floor(cal.deviceTimezoneSeconds / 3600)}`
                    : "-"}
                </Text>
              </TableRow>
              {(cal.status ?? "") === "failed" ? (
                <CalibrationErrorBlock>
                  <Text size={11} color="#c62828" type="heading" style={{ marginBottom: 4 }}>
                    Error
                  </Text>
                  <CalibrationErrorText>
                    {cal.error?.trim() ? cal.error : "(no error message stored)"}
                  </CalibrationErrorText>
                </CalibrationErrorBlock>
              ) : null}
            </React.Fragment>
          );
        })}
      </Table>

      {total > PAGE_SIZE && (
        <Pagination>
          <PageButton
            kind="outline"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </PageButton>
          <Text size={13} color="#899093">
            {offset + 1}-{Math.min(offset + PAGE_SIZE, total)} of {total}
          </Text>
          <PageButton
            kind="outline"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </PageButton>
        </Pagination>
      )}
    </div>
  );
};

export default DeviceCalibrationsSubTab;
